import { useQuery } from '@tanstack/react-query';
import { isWeekend } from 'date-fns';
import { useEffect, useState } from 'react';

import { BC_AGENTS_URL } from '../../../constants/constants';
import { useAuth } from '../../../contexts/AuthContext';
import { getTicketCheckIns } from '../queries/checkInTickets';
import { getCheckins, getMeetings } from '../queries/generateStandupSummary';
import { SummaryCheckinProps, TStandUpSummaryResponse } from '../types/types';

interface StandupSummaryInputProps {
  ticket_updates: SummaryTicketUpdateProps[];
  adhoc_updates: SummaryAdhocUpdateProps[];
  meetings: string[];
}

interface SummaryTicketUpdateProps {
  title: string;
  updates: string;
  blockers: string;
  wip_links: string;
  ticket_no: string;
  created_at: string;
}

interface SummaryAdhocUpdateProps {
  title: string;
  description: string;
  created_at: string;
}

export function useStandupSummary(): {
  generatedSummary: {
    todaysSummary: Record<string, string[]>;
    blockersSummary: string[];
    error?: string;
  };
  isLoading: boolean;
  generateSummary: () => Promise<void>;
} {
  const isAIEnabled = false;
  const { userData } = useAuth();
  const userId = userData?.id;
  const [isManualLoading, setIsManualLoading] = useState(false);
  const [generatedSummary, setGeneratedSummary] = useState({
    todaysSummary: {} as Record<string, string[]>,
    blockersSummary: [] as string[],
    error: '',
  });
  const getPreviousWorkingDay = (): string => {
    let previousDay = new Date(new Date().getTime() - 86400000)
      .toISOString()
      .split('T')[0];
    while (isWeekend(previousDay)) {
      [previousDay] = new Date(new Date(previousDay).getTime() - 86400000)
        .toISOString()
        .split('T');
    }
    return previousDay;
  };
  const fetchCheckins = async (
    previousDay: string,
    today: string,
    currentTime: string,
  ): Promise<SummaryCheckinProps[] | undefined> => {
    return getCheckins(
      `${previousDay}T${currentTime.substring(0, currentTime.length - 1)}`,
      `${today}T${currentTime.substring(0, currentTime.length - 1)}`,
      userId,
    );
  };

  const fetchStandupSummaryInput =
    async (): Promise<StandupSummaryInputProps> => {
      const [today, currentTime] = new Intl.DateTimeFormat('sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
        .format(new Date())
        .split(' ');
      const previousDay = getPreviousWorkingDay();
      const checkins = await fetchCheckins(previousDay, today, currentTime);
      const checkInData = await getTicketCheckIns(
        checkins?.map((checkin) => checkin?.id) || [],
      );
      const meetings = await getMeetings(
        `${previousDay}T${currentTime.substring(0, currentTime.length - 1)}`,
        `${today}T${currentTime.substring(0, currentTime.length - 1)}`,
        userId,
      );

      return {
        ticket_updates: checkInData
          ?.filter((item) => item.ticket_id !== null)
          .map((item) => ({
            title: item.title,
            updates: item.description,
            blockers: item.blockers || '',
            wip_links: item.wip_links || '',
            ticket_no: item.ticket.ticket_number,
            created_at: item.created_at,
          })),
        adhoc_updates: checkInData
          ?.filter((item) => item.ticket_id === null)
          ?.map((item) => ({
            title: item.title,
            description: item.description,
            created_at: item.created_at,
          })),
        meetings: meetings || [],
      };
    };

  const generateSummaryManually = (
    standupSummaryInput: StandupSummaryInputProps,
  ): TStandUpSummaryResponse => {
    const summary: Record<string, string[]> = {};
    const blockers: string[] = [];

    // Group ticket updates by ticket number and title
    const groupedTicketUpdates = new Map<string, SummaryTicketUpdateProps[]>();
    standupSummaryInput.ticket_updates.forEach((ticket) => {
      if (!groupedTicketUpdates.has(ticket.title)) {
        groupedTicketUpdates.set(ticket.title, []);
      }
      groupedTicketUpdates.get(ticket.title)?.push(ticket);
    });

    // Process each group of ticket updates
    groupedTicketUpdates.forEach((updates, key) => {
      // Sort updates by created_at in ascending order
      const sortedUpdates = updates.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      summary[key] = [];

      // Collect unique WIP links
      const uniqueWipLinks = new Set<string>();

      sortedUpdates.forEach((ticket) => {
        summary[key].push(ticket.updates);
        if (ticket.wip_links) {
          uniqueWipLinks.add(ticket.wip_links);
        }
        if (ticket.blockers) {
          blockers.push(`${ticket.ticket_no}: ${ticket.blockers}`);
        }
      });

      // Add unique WIP links at the end of the updates
      uniqueWipLinks.forEach((wipLink) => {
        summary[key].push(`WIP Link: ${wipLink}`);
      });
    });

    // Group and sort adhoc updates by title
    const groupedAdhocUpdates = new Map<string, SummaryAdhocUpdateProps[]>();
    standupSummaryInput.adhoc_updates.forEach((adhoc) => {
      if (!groupedAdhocUpdates.has(adhoc.title)) {
        groupedAdhocUpdates.set(adhoc.title, []);
      }
      groupedAdhocUpdates.get(adhoc.title)?.push(adhoc);
    });

    // Process each group of adhoc updates
    groupedAdhocUpdates.forEach((updates, title) => {
      // Sort updates by created_at in ascending order
      const sortedUpdates = updates.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      summary[title] = sortedUpdates.map((update) => update.description);
    });

    // Add meetings if present
    if (standupSummaryInput.meetings.length > 0) {
      summary.Meetings = [
        `Attended ${standupSummaryInput.meetings.join(', ')}`,
      ];
    }

    return {
      response: {
        summary,
        blockers: blockers.length > 0 ? blockers : [],
        error: '',
      },
    };
  };

  const {
    data: standupData,
    isLoading: isQueryLoading,
    refetch,
  } = useQuery<TStandUpSummaryResponse>({
    queryKey: ['standupSummary', userId],
    queryFn: async () => {
      const standupSummaryInput = await fetchStandupSummaryInput();

      // Check if all fields are empty
      const areFieldsEmpty =
        standupSummaryInput.ticket_updates.length === 0 &&
        standupSummaryInput.adhoc_updates.length === 0 &&
        standupSummaryInput.meetings.length === 0;

      if (areFieldsEmpty) {
        return {
          response: {
            summary: {},
            blockers: [],
            error: 'No updates found for the selected time period',
          },
        };
      }

      if (isAIEnabled) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds timeout

        try {
          const response = await fetch(
            `${BC_AGENTS_URL}/suggest-standup-updates`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(standupSummaryInput),
              signal: controller.signal,
            },
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(
              `Facing issues from the agent, please try again later`,
            );
          }

          interface RawResponse {
            suggestions: string[];
            summary: string;
          }

          const rawData = (await response.json()) as RawResponse;
          return {
            response: {
              summary: rawData.suggestions.reduce(
                (acc: Record<string, string[]>, suggestion: string) => {
                  acc[suggestion] = [];
                  return acc;
                },
                {},
              ),
              blockers: [],
              error: undefined,
            },
          } as TStandUpSummaryResponse;
        } catch (error) {
          clearTimeout(timeoutId);
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out after 60 seconds');
          }
          throw new Error(
            `Failed to get standup suggestions: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }
      }
      return generateSummaryManually(standupSummaryInput);
    },
  });

  useEffect(() => {
    if (standupData?.response) {
      const { summary, blockers, error } = standupData.response as {
        summary: Record<string, string[]>;
        blockers: string[];
        error?: string;
      };
      setGeneratedSummary({
        todaysSummary: summary || {},
        blockersSummary: blockers || [],
        error: error || '',
      });
    }
  }, [standupData]);

  const generateSummary = async (): Promise<void> => {
    setIsManualLoading(true);
    try {
      await refetch();
    } finally {
      setIsManualLoading(false);
    }
  };

  return {
    generatedSummary,
    isLoading: isQueryLoading || isManualLoading,
    generateSummary,
  };
}
